import { _ } from "../../core/polyglot/polyglot.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import ActionButtonWidget from "../../core/widgets/ActionButtonWidget/ActionButtonWidget.js";
import AdditionFormulaNode from "../../models/keyFigure/formula/AdditionFormulaNode.js";
import DivisionFormulaNode from "../../models/keyFigure/formula/DivisionFormulaNode.js";
import FlipWidget from "../../core/widgets/FlipWidget/FlipWidget.js";
import FormulaStackTagCreator from "./FormulaStackTagCreator.js";
import FormulaWidget from "../../core/widgets/keyFigures/FormulaWidget.js";
import jQuery from "jquery";
import MultiplicationFormulaNode from "../../models/keyFigure/formula/MultiplicationFormulaNode.js";
import NaFormulaNode from "../../models/keyFigure/formula/NaFormulaNode.js";
import optimizeFormulaNode from "../../models/keyFigure/formula/optimizeFormulaNode.js";
import popoverActionButtonWidget from "../../core/widgets/popoverActionButtonWidget.js";
import ReportReferenceFormulaNode from "../../models/reports/templates/ReportReferenceFormulaNode.js";
import StackEditorWidget from "../../core/widgets/StackEditorWidget.js";
import SubtractionFormulaNode from "../../models/keyFigure/formula/SubtractionFormulaNode.js";

/**
 * Let the user show and edit the formula of a report template
 * item. The editor first shows 2 buttons: the first buttons gives a
 * formatted overview of the formula while the second one toggles the
 * display of a menubar and a stack editor widget. When the second
 * button is clicked, the buttons become invisible.
 *
 * @param {Report} [spec.report] - Report used to look up references
 * @param {formulaTemplateNode} [spec.formula] - Initial formula to be
 *   edited
 * @param {function} canAddItemToFormula - Predicate taking a report
 *   item as argument. The function is supposed to return true if the
 *   argument can be added to the formula the receiver is creating
 * @param {dimension} [spec.dimensionTree] - Root dimension from which
 *   dimension ids are resolved
 */
export default class FormulaEditorWidget extends Widget {
	constructor({ formula, report, canAddItemToFormula, dimensionTree }) {
		super(...arguments);

		this._formula = formula || new NaFormulaNode();
		this._originalFormula = this._formula;
		this._report = report;
		this._canAddItemToFormula = canAddItemToFormula;

		this._formulaStackTagCreator = new FormulaStackTagCreator({ report });

		this.saved = this._events.createEvent();
		this.cancelled = this._events.createEvent();

		this._listEditor = new StackEditorWidget();
		this._listEditor.popped.register(() => this._removeLastFormulaNode());
		this._setListEditorItems();

		this._formulaWidget = new FormulaWidget({
			formulaNode: this._formula,
			report: this._report,
			dimensionTree,
		});

		this._formulaPopoverButton = popoverActionButtonWidget({
			label: _("Show formula"),
			dataTestName: "show-formula",
			popoverHeader: _("Formula"),
			popoverContent: this._renderFormulaPopoverOn.bind(this),
		});

		this._flipWidget = new FlipWidget({
			frontRenderable: this._renderShowAndEditButtonsOn.bind(this),
			backRenderable: this._renderEditorOn.bind(this),
		});
	}

	renderContentOn(html) {
		html.render(this._flipWidget);
	}

	_renderFormulaPopoverOn(html) {
		html.div(
			{ klass: "formula-view", "data-test": "formula-viewer" },
			this._formulaWidget,
		);
	}

	_renderShowAndEditButtonsOn(html) {
		html.render(this._formulaPopoverButton);

		html.render(
			new ActionButtonWidget({
				label: _("Edit formula"),
				dataTestName: "edit-formula",
				isPrimary: true,
				click: () => this._showEditor(),
			}),
		);
	}

	_renderEditorOn(html) {
		html.div(
			{ klass: "formula-editor", "data-test": "formula-editor" },
			this._renderMenuOn.bind(this),
			this._listEditor,
		);

		html.a(
			{
				klass: "btn btn-default btn-sm pull-right",
				"data-test": "cancel",
				click: () => this._cancel(),
			},
			_("Cancel"),
		);

		html.a(
			{
				klass: "btn btn-default btn-sm pull-right",
				"data-test": "apply",
				click: () => this._save(),
			},
			_("Apply"),
		);
	}

	_renderMenuOn(html) {
		html.ul(
			{ klass: ["menu", this._enabledCssClass()] },
			this._menuItems().map((item) => this._menuItemRenderer(item)),
		);
	}

	_menuItemRenderer(item) {
		return (html) =>
			html.li(
				{ click: item.action, "data-test": item.label },
				item.label,
			);
	}

	_showEditor() {
		this._activateReportEditorSelection();
		this._flipWidget.toggle();
	}

	_activateReportEditorSelection() {
		const selectable = jQuery(".report-editor .selectable");
		selectable.unbind("click");

		selectable.click((event) => {
			const target = jQuery(event.currentTarget);

			// We can't add the report item currently being edited to
			// its own formula:
			if (target.hasClass("selected")) {
				return;
			}

			const itemTemplateId = target.attr("reportid");
			const item =
				this._report.findItemWithItemTemplateId(itemTemplateId);

			if (item && this._canAddItemToFormula(item))
				this._addReferenceNode(
					new ReportReferenceFormulaNode({
						referenceId: itemTemplateId,
					}),
				);
		});
	}

	_save() {
		this._closeEditor().then(() => {
			this._formula = optimizeFormulaNode(this._formula);
			this._originalFormula = this._formula;
			this.saved.trigger(this._formula);
		});
	}

	_cancel() {
		this._closeEditor().then(() => {
			this._formula = this._originalFormula;
			this.cancelled.trigger();
		});
	}

	_closeEditor() {
		return this._flipWidget.showFront();
	}

	_menuItems() {
		const binaryOperatorKlasses = [
			AdditionFormulaNode,
			SubtractionFormulaNode,
			MultiplicationFormulaNode,
			DivisionFormulaNode,
		];

		return binaryOperatorKlasses.map((Class) => {
			const node = new Class();

			return {
				label: node.getLabel(),
				action: () => this._addArithmeticNode(node),
			};
		});
	}

	_addReferenceNode(node) {
		if (this._formula.isValid()) {
			return;
		}

		this._addNode(node);
	}

	_addArithmeticNode(node) {
		if (!this._formula.isValid()) {
			return;
		}

		this._addNode(node);
	}

	_addNode(node) {
		this._formula = this._formula.add(node);

		// eslint-disable-next-line no-shadow -- bad practice, we should fix this
		this._formula.forEach((node) => {
			node.resolveReferences(this._report.getTemplate());
		});

		this._setListEditorItems();
		this._formulaWidget.setFormula(this._formula);
		this.update();
	}

	_removeLastFormulaNode() {
		this._formula = this._formula.removeLastNode();
		this.update();
	}

	_setListEditorItems() {
		this._listEditor.setItems(
			this._formula.accept(this._formulaStackTagCreator),
		);
	}

	_enabledCssClass() {
		return this._formula.isValid() ? "" : "disabled";
	}
}
